import { gReactive } from '@gradin/vue-utils'
import { ref } from 'vue'
import { useApi } from '@/functions/api'
import { toast } from '@/stores/toast'

export function useSaleList () {
  const api = useApi()

  const sales = ref<SaleList[]>([])
  const pagination = ref<Pagination>()
  const loading = ref(false)

  const getData = async (params: object) => {
    try {
      loading.value = true
      const response = await api.GET<ApiCollection<SaleList>>('admin/sale', params)
      sales.value = response.data
      pagination.value = response.meta
    } finally {
      loading.value = false
    }
  }

  const getDetail = async (id: number) => {
    try {
      loading.value = true
      const response = await api.GET<SaleList>(`admin/sale/${id}`)
      return response
    } finally {
      loading.value = false
    }
  }

  return {
    sales,
    pagination,
    loading,
    getData,
    getDetail,
  }
}

export function useSaleDetail () {
  const api = useApi()

  const sale = ref<SaleDetail>()
  const loading = ref(false)

  const getData = async (id: number) => {
    try {
      loading.value = true
      const response = await api.GET<SaleDetail>(`admin/sale/${id}`)
      sale.value = response
      return response
    } finally {
      loading.value = false
    }
  }

  return {
    sale,
    loading,
    getData,
  }
}

export function useSaleUpdate () {
  const api = useApi()
  const submitting = ref(false)
  const errors = ref<FormError>({})
  const sale = ref<SaleDetail>()
  const form = gReactive({
    sale_items: [] as string[],
  })

  const submitForm = async (id: number) => {
    try {
      submitting.value = true
      const response = await api.PUT<SaleDetail>(`admin/sale/${id}`, form)
      sale.value = response
      toast.dataSaved()
      return response
    } catch (error) {
      errors.value = api.formErrors(error)
      return null
    } finally {
      submitting.value = false
    }
  }

  return {
    submitting,
    errors,
    form,
    sale,
    submitForm,
  }
}

export function useSaleCreate () {
  const api = useApi()
  const submitting = ref(false)
  const errors = ref<FormError>({})
  const sale = ref<SaleDetail>()
  const form = gReactive({
    sale_items: [] as string[],
  })

  const submitForm = async () => {
    try {
      submitting.value = true
      const response = await api.POST<SaleDetail>('admin/sale', form)
      sale.value = response
      toast.dataSaved()
      return response
    } catch (error) {
      errors.value = api.formErrors(error)
      return null
    } finally {
      submitting.value = false
    }
  }

  return {
    submitting,
    errors,
    form,
    sale,
    submitForm,
  }
}

export function useSaleDelete () {
  const api = useApi()
  const submitting = ref(false)
  const errors = ref<FormError>({})

  const submitDelete = async (id: number) => {
    try {
      submitting.value = true
      await api.DELETE(`admin/sale/${id}`)
      return id
    } catch (error) {
      errors.value = api.formErrors(error)
      return null
    } finally {
      submitting.value = false
    }
  }

  return {
    errors,
    submitting,
    submitDelete,
  }
}
